import React, { useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

/** Tab yang tersedia di card ini. "empty" = kontennya belum diimplementasikan. */
const TABS = [
  { key: "booking", label: "PROGRESS PENJUALAN" },
  { key: "stbbank", label: "PROGRESS STB BANK" },
  { key: "sp3k", label: "PROGRESS SP3K" },
];

/** Pipeline status penjualan + field tanggal yang dipakai untuk menjadikannya kolom "hari". */
const REPORT_ROWS = [
  { key: "cash", label: "CASH", dateField: "transactionat", statusDealId: null, typeIds: [3, 4] },
  { key: "booking", label: "BOOKING", dateField: "transactionat", statusDealId: 3, excludeCash: true },
  { key: "kantor", label: "KANTOR", dateField: "docat", statusDealId: 4 },
  { key: "bank", label: "BANK", dateField: "bankat", statusDealId: 5 },
  { key: "sp3k", label: "SP3K", dateField: "sp3at", statusDealId: 6 },
  { key: "sp3kfinal", label: "SP3K FINAL", dateField: "completedat", statusDealId: 7 },
  { key: "akad", label: "AKAD", dateField: "completedat", statusDealId: 8 },
];

/** Nama cabang untuk sales yang bank pengajuannya belum diisi. */
const NO_BRANCH = "TANPA CABANG";

const ACTIVE_STATUS = [2, 3, 4, 5];
const DAY_CELL_WIDTH = 46;
const STATUS_COL_WIDTH = 170;
const TOTAL_COL_WIDTH = 110;

const ROW_BG = "#ffffff";
const ROW_BG_ALT = "#f7f9fc";
const TOTAL_ROW_BG = "#e9edf3";
const HEAD_BG = "#eef2f7";
const BORDER = "#dee2e6";
const DIM_BG = "#f2f4f7";
const DIM_TEXT = "#adb5bd";
const TODAY_BG = "#e3f6fc";

const isActive = (item) => ACTIVE_STATUS.indexOf(item.statusId) !== -1;
const isCash = (item) => item.typeId === 3 || item.typeId === 4;

const matchesRow = (item, row) => {
  if (row.typeIds) return row.typeIds.indexOf(item.typeId) !== -1;
  if (row.excludeCash && isCash(item)) return false;
  if (row.statusDealId !== null && item.statusDealId !== row.statusDealId) return false;
  return true;
};

/** Daftar bulan yang boleh dipilih: Januari s.d. Desember tahun berjalan (terbaru di atas). */
const buildMonthOptions = () => {
  const months = [];
  const year = moment().format("YYYY");
  for (let m = 11; m >= 0; m--) {
    months.push(`${year}-${String(m + 1).padStart(2, "0")}`);
  }
  return months;
};

/** Ubah nilai tanggal sales menjadi indeks hari (0..daysInMonth-1) bila berada di bulan terpilih. */
const dayIndexOf = (value, salesMonth, daysInMonth) => {
  if (!value) return -1;
  const local = moment.utc(value).local();
  if (local.format("YYYY-MM") !== salesMonth) return -1;
  const day = local.date();
  return day >= 1 && day <= daysInMonth ? day - 1 : -1;
};

/** Hitung total harian, total per baris, dan grand total dari grid sel. */
const computeTotals = (rows, cells, daysInMonth) => {
  const dayTotals = new Array(daysInMonth).fill(0);
  for (const rowCells of cells) {
    for (let d = 0; d < daysInMonth; d++) dayTotals[d] += rowCells[d];
  }
  const rowTotals = cells.map((rowCells) => rowCells.reduce((a, b) => a + b, 0));
  return { rows, cells, daysInMonth, dayTotals, rowTotals, grandTotal: rowTotals.reduce((a, b) => a + b, 0) };
};

/** REPORT BOOKING: baris = status pipeline, kolom = tanggal sesuai field tanggal tiap status. */
const buildBookingReport = (saless, salesMonth) => {
  const daysInMonth = moment(salesMonth, "YYYY-MM").daysInMonth();
  const cells = REPORT_ROWS.map(() => new Array(daysInMonth).fill(0));

  for (const item of saless || []) {
    if (!isActive(item)) continue;
    for (let r = 0; r < REPORT_ROWS.length; r++) {
      const row = REPORT_ROWS[r];
      if (!matchesRow(item, row)) continue;
      const idx = dayIndexOf(item[row.dateField], salesMonth, daysInMonth);
      if (idx !== -1) cells[r][idx] += 1;
    }
  }

  return computeTotals(REPORT_ROWS, cells, daysInMonth);
};

/** Key baris untuk sebuah sales pada report per cabang: id cabang bank, atau TANPA CABANG. */
const branchKeyOf = (item) => (item.sls_bb && item.sls_bb.id != null ? String(item.sls_bb.id) : NO_BRANCH);

/**
 * Laporan per cabang bank pengajuan: baris = semua cabang (dari master), kolom = tanggal dari
 * dateField (bankat untuk STB, sp3at untuk SP3K). Cabang tanpa transaksi pada bulan terpilih
 * tetap ditampilkan dengan angka nol.
 */
const buildBranchReport = (dateField, saless, salesMonth, bankBranches) => {
  const daysInMonth = moment(salesMonth, "YYYY-MM").daysInMonth();

  const rows = [...(bankBranches || [])]
    .filter((b) => b && b.id != null && b.name)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.name).localeCompare(String(b.name)))
    .map((b) => ({ key: String(b.id), label: b.name }));

  const rowIndexByKey = new Map(rows.map((row, i) => [row.key, i]));
  const cells = rows.map(() => new Array(daysInMonth).fill(0));
  const extras = [];

  for (const item of saless || []) {
    if (!isActive(item)) continue;
    const idx = dayIndexOf(item[dateField], salesMonth, daysInMonth);
    if (idx === -1) continue;

    const key = branchKeyOf(item);
    const r = rowIndexByKey.get(key);
    if (r !== undefined) {
      cells[r][idx] += 1;
      continue;
    }

    // Cabang di luar daftar master (perusahaan lain / bank pengajuan kosong): kumpulkan dulu,
    // lalu append di bawah dengan urutan stabil (TANPA CABANG selalu paling akhir).
    let extra = extras.find((x) => x.key === key);
    if (!extra) {
      extra = { key, label: item.sls_bb && item.sls_bb.name ? item.sls_bb.name : NO_BRANCH, cells: new Array(daysInMonth).fill(0) };
      extras.push(extra);
    }
    extra.cells[idx] += 1;
  }

  extras.sort((a, b) => {
    if (a.key === NO_BRANCH) return 1;
    if (b.key === NO_BRANCH) return -1;
    return String(a.label).localeCompare(String(b.label));
  });
  for (const extra of extras) {
    rows.push({ key: extra.key, label: extra.label });
    cells.push(extra.cells);
  }

  return computeTotals(rows, cells, daysInMonth);
};

/** REPORT STB BANK: tanggal STB dari bankat. */
const buildStbBankReport = (saless, salesMonth, bankBranches) => buildBranchReport("bankat", saless, salesMonth, bankBranches);

/** REPORT SP3K: tanggal SP3K dari sp3at. */
const buildSp3kReport = (saless, salesMonth, bankBranches) => buildBranchReport("sp3at", saless, salesMonth, bankBranches);

/** Konfigurasi tampilan per tab. Tambah tab = tambah blok di sini + TABS di atas. */
const ROW_SETS = {
  booking: { firstLabel: "STATUS", lastLabel: "TOTAL", build: buildBookingReport, emptyMessage: "Belum ada data penjualan", fileSlug: "progress-penjualan" },
  stbbank: { firstLabel: "CABANG", lastLabel: "JUMLAH BANK", build: buildStbBankReport, emptyMessage: "Belum ada data cabang bank", fileSlug: "progress-stb-bank" },
  sp3k: { firstLabel: "CABANG", lastLabel: "JUMLAH SP3K", build: buildSp3kReport, emptyMessage: "Belum ada data cabang bank", fileSlug: "progress-sp3k" },
};

const ReportBooking = ({ saless, salesLoading, bankBranches, height = "550px" }) => {
  const [activeTab, setActiveTab] = useState("booking");
  const [salesMonth, setSalesMonth] = useState(moment().format("YYYY-MM"));
  const months = useMemo(() => buildMonthOptions(), []);

  const activeTabMeta = TABS.find((t) => t.key === activeTab) || TABS[0];
  const activeCfg = ROW_SETS[activeTab] || ROW_SETS.booking;

  const report = useMemo(() => activeCfg.build(saless, salesMonth, bankBranches), [activeCfg, saless, salesMonth, bankBranches]);
  const { rows, cells, daysInMonth, dayTotals, rowTotals, grandTotal } = report;

  const bodyMaxHeight = useMemo(() => {
    const parsed = parseInt(height, 10);
    return `${(isNaN(parsed) ? 530 : parsed) - 120}px`;
  }, [height]);

  const today = moment();
  const todayMonth = today.format("YYYY-MM");
  const isCurrentMonth = salesMonth === todayMonth;
  const todayIndex = today.date() - 1;

  const hasSales = !!saless && saless.length > 0;
  const hasRows = rows.length > 0;
  const isLoading = !!salesLoading && !hasSales;

  /** Kolom tanggal yang belum tercapai: bulan depan semua, bulan ini hanya setelah hari ini. */
  const isFutureDay = (d) => {
    if (salesMonth < todayMonth) return false;
    if (salesMonth > todayMonth) return true;
    return d + 1 > today.date();
  };
  /** Gaya sel tanggal: biru untuk hari ini, redup untuk hari yang belum tercapai. */
  const dayBg = (d) => (isCurrentMonth && d === todayIndex ? TODAY_BG : isFutureDay(d) ? DIM_BG : null);

  const dayStyle = (extra) => ({ minWidth: DAY_CELL_WIDTH, width: DAY_CELL_WIDTH, ...extra });
  const firstCellStyle = (bg, zIndex) => ({
    position: "sticky",
    left: 0,
    zIndex,
    minWidth: STATUS_COL_WIDTH,
    width: STATUS_COL_WIDTH,
    background: bg,
    boxShadow: `inset -1px 0 0 0 ${BORDER}`,
  });
  const lastCellStyle = (bg, zIndex) => ({
    position: "sticky",
    right: 0,
    zIndex,
    minWidth: TOTAL_COL_WIDTH,
    width: TOTAL_COL_WIDTH,
    background: bg,
    boxShadow: `inset 1px 0 0 0 ${BORDER}`,
  });

  /** Unduh tab yang sedang aktif ke Excel. Baris judul = nama laporan + bulan. */
  const handleExportExcel = () => {
    if (!rows.length) {
      toast.error("Data kosong atau tidak ada, export dibatalkan.", { autoClose: 3000 });
      return;
    }

    const monthLabel = moment(salesMonth, "YYYY-MM").format("MMMM YYYY");
    const header = [activeCfg.firstLabel, ...Array.from({ length: daysInMonth }, (_, d) => d + 1), activeCfg.lastLabel];
    const body = rows.map((row, r) => [row.label, ...cells[r], rowTotals[r]]);
    body.push(["TOTAL", ...dayTotals, grandTotal]);

    const aoa = [[`${activeTabMeta.label} - ${monthLabel}`], header, ...body];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Lebar kolom dihitung dari header + body saja. Baris 0 (judul) sengaja dikecualikan
    // supaya kolom label tidak melebar gara-gara teks judul laporan.
    const dataRows = aoa.slice(1);
    ws["!cols"] = header.map((_, colIndex) => ({
      wch: Math.max(...dataRows.map((r) => (r[colIndex] != null ? String(r[colIndex]).length : 0))) + 2,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${activeCfg.fileSlug}-${moment(salesMonth, "YYYY-MM").format("MMMM-YYYY")}.xlsx`);
  };

  const renderEmpty = () => (
    <CardBody className="pt-0">
      <div className="text-center text-muted fs-13 py-5">
        {isLoading ? "Memuat data..." : activeTabMeta.empty ? `${activeTabMeta.label} belum tersedia` : activeCfg.emptyMessage}
      </div>
    </CardBody>
  );

  const renderTable = () => (
    <CardBody className="pt-0">
      <div style={{ maxHeight: bodyMaxHeight, overflowX: "auto", overflowY: "auto" }}>
        <table className="table table-bordered fs-13 mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, width: "max-content", minWidth: "100%" }}>
          <thead>
            <tr>
              <th className="fs-14 text-start align-middle" style={firstCellStyle(HEAD_BG, 4)}>
                {activeCfg.firstLabel}
              </th>
              {Array.from({ length: daysInMonth }, (_, d) => (
                <th
                  key={d}
                  className={`fs-14 text-center align-middle ${isCurrentMonth && d === todayIndex ? "text-info fw-semibold bg-soft-info" : ""}`}
                  style={dayStyle({
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    background: isFutureDay(d) ? DIM_BG : HEAD_BG,
                    color: isFutureDay(d) ? DIM_TEXT : undefined,
                    boxShadow: isCurrentMonth && d === todayIndex ? "inset 0 0 0 2px #0dcaf0" : "none",
                  })}>
                  {d + 1}
                </th>
              ))}
              <th className="fs-14 text-center align-middle" style={lastCellStyle(HEAD_BG, 4)}>
                {activeCfg.lastLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => {
              const bg = r % 2 === 0 ? ROW_BG : ROW_BG_ALT;
              return (
                <tr key={row.key} style={{ background: bg }}>
                  <th className="fs-14 fw-semibold align-middle text-start" style={firstCellStyle(bg, 2)}>
                    {row.label}
                  </th>
                  {Array.from({ length: daysInMonth }, (_, d) => {
                    const value = cells[r][d];
                    return (
                      <td key={d} className={`text-center fs-13 ${value > 0 ? "fw-semibold text-dark" : "text-muted"}`} style={dayStyle(dayBg(d) ? { background: dayBg(d) } : null)}>
                        {value}
                      </td>
                    );
                  })}
                  <td className="text-center fs-13 fw-semibold" style={lastCellStyle(bg, 2)}>
                    {rowTotals[r]}
                  </td>
                </tr>
              );
            })}
            <tr>
              <th className="fs-14 fw-semibold align-middle text-start" style={firstCellStyle(TOTAL_ROW_BG, 2)}>
                TOTAL
              </th>
              {Array.from({ length: daysInMonth }, (_, d) => (
                <td
                  key={d}
                  className={`text-center fs-13 fw-semibold ${isCurrentMonth && d === todayIndex ? "text-info bg-soft-info" : "text-dark"}`}
                  style={dayStyle({ background: isFutureDay(d) ? DIM_BG : TOTAL_ROW_BG })}>
                  {dayTotals[d]}
                </td>
              ))}
              <td className="text-center fs-13 fw-bold" style={lastCellStyle(TOTAL_ROW_BG, 2)}>
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </CardBody>
  );

  return (
    <Card className="" style={{ height }}>
      <CardHeader className="border-0 mb-0 align-items-center d-flex">
        <h4 className="card-title mb-0 flex-grow-1 text-uppercase">{activeTabMeta.label} {moment(salesMonth, "YYYY-MM").format("MMMM YYYY")}</h4>
        <div className="flex-shrink-0 d-flex align-items-center gap-2">
          <button type="button" className="btn btn-sm btn-success flex-shrink-0" onClick={handleExportExcel}>
            <i className="ri-file-excel-line me-1 align-bottom"></i> Excel
          </button>
          <select
            className="form-select form-select-sm fs-13"
            style={{ minWidth: "150px" }}
            value={salesMonth}
            onChange={(e) => setSalesMonth(e.target.value)}
            aria-label="Pilih bulan">
            {months.map((m) => (
              <option key={m} value={m}>
                {moment(m, "YYYY-MM").format("MMMM YYYY")}
              </option>
            ))}
          </select>
        </div>
      </CardHeader>
      <div className="px-3 pb-2 border-bottom">
        <Nav pills className="nav nav-pills nav-primary bg-light">
          {TABS.map((tab) => (
            <NavItem key={tab.key}>
              <NavLink
                href="#"
                style={{ fontSize: "13px", cursor: "pointer", padding: "8px 14px" }}
                className={classnames({ active: activeTab === tab.key })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab(tab.key);
                }}>
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      {activeTabMeta.empty || !hasSales || !hasRows ? renderEmpty() : renderTable()}
    </Card>
  );
};

export default ReportBooking;
